import { Fragment, PureComponent, ReactNode } from 'react';
import Hotkeys from 'react-hot-keys';
import { motion } from 'framer-motion';
import { CloseBtIcon } from '../../components/prescriptionPad/rightPanel/finishScreen/BookLabTestsUtils';
import PrescribeAutoSuggestV2 from '../../components/prescriptionPad/rightPanel/workspace/PrescribeAutoSuggestV2';
import ValidationItem from '../../components/prescriptionPad/rightPanel/workspace/ValidationItem';
import AntAutoCompleteV2 from '../../components/prescriptionPad/rightPanel/workspace/padV2/AntAutoComplete';
import { onFormSearch } from '../../components/prescriptionPad/rightPanel/workspace/padV2/onFormSearch';
import { onGenericCompositionSearch } from '../../components/prescriptionPad/rightPanel/workspace/padV2/onGenericCompositionSearch';
import { onManufacturerSearch } from '../../components/prescriptionPad/rightPanel/workspace/padV2/onManufacturerSearch';
import { MedicationsEntity, MedicineCategory } from '../../store/types/prescription';
import { LOCAL_ID_PREFIX } from '../../constants';
import MedTableRenderer from '../../components/prescriptionPad/rightPanel/workspace/padV2/MedTableRenderer';
import { connect } from 'react-redux';
import { GlobalState } from '../../store/reducers';
import { getGenieSetting } from '../../store/selectors/session';
import { GeniePadElementsSettingItem } from '../../api/pushGenie';
import { MY_PROFILE_CONTAINER } from '../Settings';
import { EkaFocosableElement } from '../../components/common/types/EkaFocusable';
import { Checkbox, notification, Upload } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import uploadRenderPdfImages from '../../api/uploadRenderPdfImages';
import { getDoc } from '../../store/idbHandler';
import { uploadIcon } from '../../components/common/Icons';
import fetchDrugSearch, { CodedMedications } from '../../api/fetchDrugSearch';
import {
    FROM_GENIE_DASHBOARD,
    GD_ADD_MEDICINES,
} from '../../components/prescriptionPad/trackSource';
import { apiDrugToMedicationEntity } from '../../components/prescriptionPad/rightPanel/workspace/PrescribeAutoSuggestV2';
import { notMedicineObj } from './GenieConfiguration';
import { track } from '../../tracking';
import { captureError, captureErrorString } from '../captureError';
import { GMedication } from '../../store/db';
import { hashCode } from '../../store/utils';
import { notADrugObj } from './ListGenieItems';

type State = {
    selectedMed: MedicationsEntity | null;
    link?: string;
    image?: string;
    imageFile?: File;
    isMedicineImageUploading?: boolean;
    matchingDrugs?: CodedMedications[];
    isSaveMedLoading?: boolean;
    isSaveAndAddMedLoading?: boolean;
};

type Props = {
    closeMe: () => void;
    elementsConfig: GeniePadElementsSettingItem[];
    onAddMedicine: (med: GenieAddMedicine, isAddMedicinesDialogVisible?: boolean) => Promise<void>;
};

interface ExtraMedKeys {
    link?: string;
    image?: string;
}

export type GenieAddMedicine = GMedication & ExtraMedKeys;

const mrUploadIcon = uploadIcon('fill-darwin-primary-500');

class AddMedicines extends PureComponent<Props, State> {
    REF_MAP: EkaFocosableElement[][] = [];

    initialState: State = {
        selectedMed: null,
        link: undefined,
        image: undefined,
        imageFile: undefined,
        matchingDrugs: undefined,
        isSaveMedLoading: undefined,
        isSaveAndAddMedLoading: undefined,
    };
    state: State = this.initialState;

    onFormChange = (form?: string): void => {
        const { selectedMed } = this.state;

        if (!selectedMed) return;

        const newMed = { ...selectedMed, form: form, dosage_form: form };
        this.setState({
            selectedMed: newMed,
        });

        track('GENIE_DASHBOARD_ADD_MEDICINES', {
            propertyChanged: 'form',
            id: newMed.id,
            name: newMed.name,
            value: form,
        });
    };

    onGenericNameChange = (generic?: { id: string; name: string }): void => {
        const { selectedMed } = this.state;

        if (!selectedMed) return;

        const newMed = { ...selectedMed, generic_name: generic?.name, generic_id: generic?.id };
        this.setState({
            selectedMed: newMed,
        });

        track('GENIE_DASHBOARD_ADD_MEDICINES', {
            propertyChanged: 'generic name',
            id: newMed.id,
            name: newMed.name,
            value: generic?.name,
        });
    };

    onManufacturerNameChange = (manufacturer?: { id: string; name: string }): void => {
        const { selectedMed } = this.state;

        if (!selectedMed) return;

        const newMed = {
            ...selectedMed,
            manufacturer_name: manufacturer?.name,
            manufacturer_id: manufacturer?.id,
        };
        this.setState({
            selectedMed: newMed,
        });

        track('GENIE_DASHBOARD_ADD_MEDICINES', {
            propertyChanged: 'manufacturer name',
            id: newMed.id,
            name: newMed.name,
            value: manufacturer?.name,
        });
    };

    onOTCChange = (e: CheckboxChangeEvent): void => {
        const { selectedMed } = this.state;

        if (!selectedMed) return;

        const newMed = { ...selectedMed, is_otc: e.target.checked };
        this.setState({ selectedMed: newMed });

        track('GENIE_DASHBOARD_ADD_MEDICINES', {
            propertyChanged: 'otc',
            id: newMed.id,
            name: newMed.name,
            value: String(!selectedMed.is_otc),
        });
    };

    onImageUpload = async (file: File) => {
        const { selectedMed } = this.state;
        const docId = getDoc()?._id;

        if (!file || !selectedMed) return;

        const formData = new FormData();
        const currentTimestamp = new Date().getTime();
        formData.append('header-footer', file, `${docId}-${selectedMed.id}-${currentTimestamp}`);

        this.setState({ isMedicineImageUploading: true, imageFile: file });

        try {
            const { success, url, error = '' } = await uploadRenderPdfImages(formData, true);

            if (success) {
                this.setState({
                    image: url,
                    isMedicineImageUploading: false,
                });
            } else {
                this.setState({ isMedicineImageUploading: false, imageFile: undefined });
                notification.error({
                    message: 'Failed to upload Medicine Image',
                    duration: 5,
                });
                captureErrorString(error);
                track('ERROR', { on: 'uploadMedicineImage', what: error });
            }
        } catch (error) {
            this.setState({ isMedicineImageUploading: false, imageFile: undefined });
            notification.error({
                message: 'Failed to upload Medicine Image',
                duration: 5,
            });
            captureError(error as Error);
            track('ERROR', { on: 'uploadMedicineImage', what: (error as Error).message });
        }
    };

    fetchSuggestions = async (med: MedicationsEntity): Promise<void> => {
        try {
            const res = await fetchDrugSearch(
                {
                    medications: [
                        {
                            name: med.name,
                            id: med.id,
                            form: med.dosage_form || med.form,
                            unit: med.dose?.unit,
                            uid: med.dose?.unit,
                            generic_name: med.generic_name,
                            generic_id: med.generic_id,
                            manufacturer_name: med.manufacturer_name,
                            manufacturer_id: med.manufacturer_id,
                            frequency: med.frequency,
                            duration: med.duration,
                        },
                    ],
                },
                FROM_GENIE_DASHBOARD,
            );

            this.setState({ matchingDrugs: res[med.id] });
        } catch (error) {
            captureError(error as Error);
            track('ERROR', { on: 'fetchDrugSearch', what: (error as Error).message });
        }
    };

    onSuggestedSelect = (medicine: CodedMedications): void => {
        const { selectedMed } = this.state;

        if (!selectedMed) return;

        const apiToMed = apiDrugToMedicationEntity(medicine);

        const newMed = {
            ...apiToMed,
            ...selectedMed,
            id: medicine.id,
            name: medicine.name,
            form: '',
            dosage_form: medicine.dosage?.dosage_form || '',
            df_id: medicine.dosage?.df_id,
            generic_id: medicine.generic_id,
            generic_name: medicine.generic_name,
            product_type: medicine.product_type,
            soa: medicine.soa || false,
            manufacturer_name: medicine.manufacturer_name,
            hxng_mapped: medicine.hxng_mapped,
            hxng_status: medicine.hxng_status,
            med_cat: undefined,
        };

        if (selectedMed.df_id === medicine.dosage.df_id) {
            newMed.dose = selectedMed.dose;
        } else {
            newMed.dose = apiToMed.dose;
        }

        this.setState({
            selectedMed: newMed,
            matchingDrugs: undefined,
        });

        track('GENIE_DASHBOARD_ADD_MEDICINES_SUGGESTION_SELECT', {
            newMed: medicine.id,
            newMedName: medicine.name,
            currentMed: selectedMed.id,
            currentMedName: selectedMed.name,
            labelForTracking: GD_ADD_MEDICINES,
        });
    };

    onNotADrugSelect = (med_cat: MedicineCategory): void => {
        const { selectedMed } = this.state;

        if (!selectedMed) return;

        const newMed = {
            ...selectedMed,
            med_cat,
            currentItemStatus: 'nad',
        };

        this.setState({
            selectedMed: newMed,
            matchingDrugs: undefined,
        });

        track('GENIE_DASHBOARD_ADD_MEDICINES', {
            propertyChanged: 'med_cat',
            id: selectedMed.id,
            value: med_cat,
        });
    };

    onAutoSuggestMedSelect = (med: MedicationsEntity) => {
        const isLocalDrug = med.id.startsWith(LOCAL_ID_PREFIX);
        if (isLocalDrug) {
            med.id = `${LOCAL_ID_PREFIX}ad-${hashCode(med.name.trim())}`;
        }

        this.setState({
            selectedMed: med,
        });

        if (isLocalDrug) this.fetchSuggestions(med);

        track('GENIE_DASHBOARD_ADD_MEDICINES', {
            action: 'Select Medicine from Autosuggest',
            id: med.id,
            name: med.name,
        });
    };

    onAddMedicine = async (trackAction: string, doNotCloseModal?: boolean) => {
        const { onAddMedicine } = this.props;
        const { selectedMed, link, image } = this.state;

        if (!selectedMed) return;
        const { id, name, med_cat } = selectedMed;
        const isLocal = id.startsWith(LOCAL_ID_PREFIX) ? 1 : 0;
        const currentMedStatus = med_cat ? 'nad' : isLocal ? 'uncoded' : 'coded';

        await onAddMedicine(
            {
                ...selectedMed,
                link,
                image,
                meta: {
                    isLocal,
                    updatedAt: new Date().getTime(),
                    fCount: 0,
                    do_not_purge: true,
                },
            },
            doNotCloseModal,
        );
        this.setState({ ...this.initialState });

        track('GENIE_DASHBOARD_ADD_MEDICINES', {
            id,
            name,
            currentMedStatus,
            link,
            image,
            med_cat,
            action: trackAction,
        });
    };

    render(): ReactNode {
        const { closeMe, elementsConfig } = this.props;
        const {
            selectedMed,
            link,
            isMedicineImageUploading,
            matchingDrugs,
            imageFile,
            isSaveMedLoading,
            isSaveAndAddMedLoading,
        } = this.state;
        const { onAddMedicine } = this;

        const isFormValid = (selectedMed?.dosage_form || selectedMed?.form || '')?.length > 0;
        const isGenericValid = (selectedMed?.generic_name || '')?.length > 0;

        const isInfoFilled =
            (isFormValid && isGenericValid) || selectedMed?.is_otc || selectedMed?.med_cat;
        const isLocalDrug = selectedMed?.id.startsWith(LOCAL_ID_PREFIX);

        const notMedicineStatus = matchingDrugs && matchingDrugs[0]?.non_medicine_status;
        const displayImage = imageFile ? URL.createObjectURL(imageFile) : '';

        const disabledBtns =
            !selectedMed || isMedicineImageUploading || isSaveMedLoading || isSaveAndAddMedLoading;

        return (
            <Hotkeys keyName="esc">
                <motion.div
                    initial={{ opacity: 0 }}
                    exit={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    style={{ zIndex: 20 }}
                    transition={{ duration: 0.2, ease: 'linear' }}
                    className="font-lato select-none"
                >
                    <div className="modal-overlay" onClick={closeMe}></div>
                    <div
                        className="modal-content max-h-592 h-4/5 font-lato font-400 rounded-8"
                        style={{ width: '1000px' }}
                    >
                        <div className="h-full flex flex-col">
                            <div className="flex items-center justify-between px-16 py-12 rounded-t-8">
                                <div className="flex items-center space-x-8">
                                    <div className="flex items-center justify-center h-28 w-28 bg-darwin-accent-medicines-pink-200 rounded-full">
                                        <p className="font-700 text-darwin-accent-medicines-pink-800">
                                            Rx
                                        </p>
                                    </div>
                                    <p className="font-700 text-16">Add medicine</p>
                                </div>

                                <CloseBtIcon closeMe={closeMe} />
                            </div>

                            <div className="flex-1 flex flex-col p-16 overflow-scroll space-y-12 border border-darwin-neutral-200">
                                {selectedMed ? (
                                    <div
                                        className="flex items-center justify-between px-12 space-x-8 border border-neutral-5 w-full bg-transparent rounded-8"
                                        style={{ minHeight: '42px' }}
                                    >
                                        <div className="flex items-center space-x-12">
                                            <svg
                                                className="w-16 h-16 fill-darwin-neutral-400"
                                                role="img"
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 512 512"
                                            >
                                                <path d="M504.1 471l-134-134C399.1 301.5 415.1 256.8 415.1 208c0-114.9-93.13-208-208-208S-.0002 93.13-.0002 208S93.12 416 207.1 416c48.79 0 93.55-16.91 129-45.04l134 134C475.7 509.7 481.9 512 488 512s12.28-2.344 16.97-7.031C514.3 495.6 514.3 480.4 504.1 471zM48 208c0-88.22 71.78-160 160-160s160 71.78 160 160s-71.78 160-160 160S48 296.2 48 208z"></path>
                                            </svg>
                                            <p className="flex-1">
                                                {selectedMed.name}{' '}
                                                {selectedMed.med_cat && (
                                                    <span className="capitalize">
                                                        ({notMedicineObj[selectedMed.med_cat]})
                                                    </span>
                                                )}
                                            </p>
                                        </div>

                                        <svg
                                            className="w-12 h-12 fill-darwin-neutral-400 cursor-pointer"
                                            viewBox="0 0 12 12"
                                            xmlns="http://www.w3.org/2000/svg"
                                            onClick={() => this.setState({ ...this.initialState })}
                                        >
                                            <path d="M10.75 9.71875C11.0312 10.0312 11.0312 10.5 10.75 10.7812C10.4375 11.0938 9.96875 11.0938 9.6875 10.7812L6 7.0625L2.28125 10.7812C1.96875 11.0938 1.5 11.0938 1.21875 10.7812C0.90625 10.5 0.90625 10.0312 1.21875 9.71875L4.9375 6L1.21875 2.28125C0.90625 1.96875 0.90625 1.5 1.21875 1.21875C1.5 0.90625 1.96875 0.90625 2.25 1.21875L6 4.96875L9.71875 1.25C10 0.9375 10.4688 0.9375 10.75 1.25C11.0625 1.53125 11.0625 2 10.75 2.3125L7.03125 6L10.75 9.71875Z" />
                                        </svg>
                                    </div>
                                ) : (
                                    <PrescribeAutoSuggestV2
                                        kind={'Medicines'}
                                        onSelect={(drug) => {
                                                this.onAutoSuggestMedSelect(drug as MedicationsEntity);
                                        }}
                                        isForAddMedicines
                                    />
                                )}
                                {selectedMed ? (
                                    <Fragment>
                                        <div className="border border-darwin-neutral-100">
                                            <div
                                                className={`flex justify-between w-full items-center py-8 px-16 ${
                                                    isInfoFilled || !isLocalDrug
                                                        ? 'bg-darwin-accent-green-100'
                                                        : 'bg-darwin-status-warning-100'
                                                }`}
                                            >
                                                <p className="text-darwin-neutral-800 text-12 font-700 uppercase">
                                                    Medicine details
                                                </p>

                                                <div className="flex items-center space-x-16">
                                                    {isLocalDrug && (
                                                        <Fragment>
                                                            <Checkbox
                                                                onChange={this.onOTCChange}
                                                                value={selectedMed.is_otc}
                                                                className="text-14 text-neutral-1000 font-400"
                                                            >
                                                                Mark as OTC
                                                            </Checkbox>

                                                            <ValidationItem
                                                                isValid={isFormValid}
                                                                text="Form"
                                                            />
                                                            <ValidationItem
                                                                isValid={isGenericValid}
                                                                text="Generic"
                                                            />
                                                        </Fragment>
                                                    )}
                                                </div>
                                            </div>

                                            <div
                                                className={`m-16 gap-x-12 gap-y-4 ${
                                                    isLocalDrug
                                                        ? 'grid grid-cols-3'
                                                        : 'flex flex-wrap'
                                                }`}
                                                id={selectedMed.id}
                                            >
                                                {isLocalDrug ? (
                                                    <AntAutoCompleteV2
                                                        placement={'bottomLeft'}
                                                        onSearch={onFormSearch}
                                                        selectedValue={
                                                            selectedMed.dosage_form ||
                                                            selectedMed.form
                                                        }
                                                        debounceDelay={200}
                                                        getLabelToCompare={(o) => o}
                                                        placeholder={'Form'}
                                                        onSelect={this.onFormChange}
                                                        customDropdownWidth={150}
                                                    />
                                                ) : (
                                                    <p className="flex space-x-8">
                                                        <span className="font-700">Form:</span>
                                                        <span>
                                                            {selectedMed.dosage_form ||
                                                                selectedMed.form ||
                                                                '—'}
                                                        </span>
                                                    </p>
                                                )}

                                                {isLocalDrug ? (
                                                    <AntAutoCompleteV2
                                                        placement={'bottomLeft'}
                                                        onSearch={onGenericCompositionSearch}
                                                        selectedValue={
                                                            selectedMed.generic_name
                                                                ? {
                                                                      name:
                                                                          selectedMed.generic_name ||
                                                                          '',
                                                                      id:
                                                                          selectedMed.generic_id ||
                                                                          '',
                                                                  }
                                                                : undefined
                                                        }
                                                        debounceDelay={200}
                                                        getLabelToCompare={(o) => o.name}
                                                        placeholder={'Generic'}
                                                        onSelect={(option) => {
                                                            this.onGenericNameChange(option);
                                                        }}
                                                        customDropdownWidth={300}
                                                    />
                                                ) : (
                                                    <p className="flex space-x-8">
                                                        <span className="font-700">Generic:</span>
                                                        <span>
                                                            {selectedMed.generic_name || '—'}
                                                        </span>
                                                    </p>
                                                )}

                                                {isLocalDrug ? (
                                                    <AntAutoCompleteV2
                                                        placement={'bottomLeft'}
                                                        onSearch={onManufacturerSearch}
                                                        selectedValue={
                                                            selectedMed.manufacturer_name
                                                                ? {
                                                                      name:
                                                                          selectedMed.manufacturer_name ||
                                                                          '',
                                                                      id:
                                                                          selectedMed.manufacturer_id ||
                                                                          '',
                                                                  }
                                                                : undefined
                                                        }
                                                        debounceDelay={200}
                                                        getLabelToCompare={(o) => o.name}
                                                        placeholder={'Manufacturer'}
                                                        onSelect={(option) => {
                                                            this.onManufacturerNameChange(option);
                                                        }}
                                                        customDropdownWidth={200}
                                                    />
                                                ) : (
                                                    <p className="flex space-x-8">
                                                        <span className="font-700">
                                                            Manufacturer:
                                                        </span>
                                                        <span>
                                                            {selectedMed.manufacturer_name || '—'}
                                                        </span>
                                                    </p>
                                                )}
                                            </div>

                                            {matchingDrugs && matchingDrugs.length && (
                                                <div className="flex items-center space-x-16 m-16 p-16 bg-darwin-neutral-50 rounded-8">
                                                    <span className="text-darwin-neutral-900 italic whitespace-nowrap">
                                                        Did you mean
                                                    </span>
                                                    <div className="flex space-x-16">
                                                        {notMedicineStatus ? (
                                                            <Fragment>
                                                                <div
                                                                    className="font-400 text-13 text-darwin-primary-500 whitespace-nowrap cursor-pointer rounded-full border-darwin-primary-500 border-1 py-6 px-16"
                                                                    onClick={() =>
                                                                        this.onNotADrugSelect(
                                                                            notMedicineStatus,
                                                                        )
                                                                    }
                                                                >
                                                                    It's{' '}
                                                                    {notADrugObj[notMedicineStatus]}
                                                                </div>

                                                                <div
                                                                    className="font-400 text-13 text-darwin-primary-500 whitespace-nowrap cursor-pointer rounded-full border-darwin-primary-500 border-1 py-6 px-16"
                                                                    onClick={() =>
                                                                        this.onNotADrugSelect('nad')
                                                                    }
                                                                >
                                                                    Not a Drug
                                                                </div>
                                                            </Fragment>
                                                        ) : (
                                                            matchingDrugs.map((med) => (
                                                                <div
                                                                    key={med.id}
                                                                    onClick={() =>
                                                                        this.onSuggestedSelect(med)
                                                                    }
                                                                    className="cursor-pointer rounded-full border-darwin-primary-500 border-1 py-6 px-16"
                                                                >
                                                                    <div className="flex space-x-12 items-center">
                                                                        <span className="whitespace-nowrap font-400 text-13 text-darwin-primary-500">
                                                                            {med.name}
                                                                        </span>
                                                                        {med.dosage
                                                                            ?.dosage_form && (
                                                                            <span className="whitespace-nowrap text-darwin-neutral-700 px-4 py-2 rounded-4 border border-darwin-neutral-200 text-10">
                                                                                {
                                                                                    med.dosage
                                                                                        .dosage_form
                                                                                }
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    <span className="max-w-400 font-400 text-12 text-darwin-neutral-600 mt-2 truncate-overflow-2">
                                                                        {med.generic_name}
                                                                    </span>
                                                                </div>
                                                            ))
                                                        )}
                                                    </div>
                                                </div>
                                            )}

                                            {isLocalDrug && (
                                                <div className="m-16 p-16 space-y-16 bg-darwin-neutral-50 rounded-8">
                                                    <div className="flex items-start space-x-8">
                                                        <div className="pt-3">
                                                            <svg
                                                                width="16"
                                                                height="14"
                                                                viewBox="0 0 16 14"
                                                                fill="none"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                            >
                                                                <path
                                                                    d="M1.46875 8.40625C0.53125 7.53125 0 6.28125 0 4.96875V4.8125C0 2.625 1.5625 0.75 3.71875 0.40625C5.15625 0.15625 6.59375 0.625 7.625 1.625L8 2L8.375 1.625C9.375 0.625 10.8438 0.15625 12.25 0.40625C14.4062 0.75 16 2.625 16 4.8125V4.96875C16 6.28125 15.4375 7.53125 14.5 8.40625L8.84375 13.6875C8.625 13.9062 8.3125 14 8 14C7.65625 14 7.34375 13.9062 7.125 13.6875L1.46875 8.40625Z"
                                                                    fill="#1B9C7D"
                                                                />
                                                            </svg>
                                                        </div>

                                                        <p className="">
                                                            Add a photo of this medicine or link to
                                                            its details &amp; contribute to{' '}
                                                            <span className="font-700">
                                                                India’s National Medicine Database!
                                                            </span>
                                                        </p>
                                                    </div>

                                                    <div className="flex space-x-16 items-center">
                                                        {displayImage ? (
                                                            <div className="relative">
                                                                <img
                                                                    className="object-contain h-150 min-w-160 rounded-8"
                                                                    src={displayImage}
                                                                />
                                                                <button
                                                                    className="absolute -right-10 -top-10 p-6 rounded-full bg-darwin-neutral-100 shadow-elevation-900 cursor-pointer"
                                                                    onClick={() => {
                                                                        this.setState({
                                                                            image: undefined,
                                                                            imageFile: undefined,
                                                                        });
                                                                    }}
                                                                    disabled={
                                                                        !selectedMed ||
                                                                        isMedicineImageUploading
                                                                    }
                                                                >
                                                                    <svg
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                        viewBox="0 0 320 512"
                                                                        fill="currentColor"
                                                                        className="w-16 h-16 text-darwin-neutral-1000"
                                                                    >
                                                                        <path d="M312.1 375c9.369 9.369 9.369 24.57 0 33.94s-24.57 9.369-33.94 0L160 289.9l-119 119c-9.369 9.369-24.57 9.369-33.94 0s-9.369-24.57 0-33.94L126.1 256L7.027 136.1c-9.369-9.369-9.369-24.57 0-33.94s24.57-9.369 33.94 0L160 222.1l119-119c9.369-9.369 24.57-9.369 33.94 0s9.369 24.57 0 33.94L193.9 256L312.1 375z" />
                                                                    </svg>
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <Upload
                                                                id="med-pic"
                                                                name="file"
                                                                method="post"
                                                                listType="picture"
                                                                accept="image/png, image/jpeg, image/apng, image/avif, image/gif, image/svg+xml, image/webp"
                                                                multiple={false}
                                                                showUploadList={false}
                                                                customRequest={(e) =>
                                                                    this.onImageUpload(
                                                                        e.file as File,
                                                                    )
                                                                }
                                                            >
                                                                <button className="btn--small secondary-btn px-16 py-8 space-x-8">
                                                                    <span>
                                                                        {isMedicineImageUploading ? (
                                                                            <svg
                                                                                className="w-16 h-16 fill-darwin-primary-500 animate-spin"
                                                                                xmlns="http://www.w3.org/2000/svg"
                                                                                viewBox="0 0 512 512"
                                                                            >
                                                                                <path d="M96 256c0-26.5-21.5-48-48-48S0 229.5 0 256s21.5 48 48 48S96 282.5 96 256zM108.9 60.89c-26.5 0-48.01 21.49-48.01 47.99S82.39 156.9 108.9 156.9s47.99-21.51 47.99-48.01S135.4 60.89 108.9 60.89zM108.9 355.1c-26.5 0-48.01 21.51-48.01 48.01S82.39 451.1 108.9 451.1s47.99-21.49 47.99-47.99S135.4 355.1 108.9 355.1zM256 416c-26.5 0-48 21.5-48 48S229.5 512 256 512s48-21.5 48-48S282.5 416 256 416zM464 208C437.5 208 416 229.5 416 256s21.5 48 48 48S512 282.5 512 256S490.5 208 464 208zM403.1 355.1c-26.5 0-47.99 21.51-47.99 48.01S376.6 451.1 403.1 451.1s48.01-21.49 48.01-47.99S429.6 355.1 403.1 355.1zM256 0C229.5 0 208 21.5 208 48S229.5 96 256 96s48-21.5 48-48S282.5 0 256 0z"></path>
                                                                            </svg>
                                                                        ) : (
                                                                            mrUploadIcon
                                                                        )}
                                                                    </span>
                                                                    <span>Upload photo</span>
                                                                </button>
                                                            </Upload>
                                                        )}

                                                        <p>Or</p>

                                                        <input
                                                            value={link}
                                                            onChange={(e) =>
                                                                this.setState({
                                                                    link: e.target.value,
                                                                })
                                                            }
                                                            className="flex-1 px-12 py-10 border border-neutral-7 rounded-8"
                                                            placeholder="Weblink for the medicine"
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        <div className="border border-darwin-neutral-100">
                                            <p className="py-8 px-16 bg-darwin-neutral-50 w-fulltext-darwin-neutral-800 text-12 font-700 uppercase">
                                                Dose details
                                            </p>

                                            <div className="m-16">
                                                <MedTableRenderer
                                                    kind="Medicines"
                                                    elements={selectedMed ? [selectedMed] : []}
                                                    elementsConfig={elementsConfig}
                                                    upsertElement={(med) => {
                                                        this.setState({
                                                            selectedMed: med,
                                                        });
                                                    }}
                                                    isNameEditable={false}
                                                    containerId={MY_PROFILE_CONTAINER}
                                                    REF_MAP={this.REF_MAP}
                                                    hideFixNowStrip
                                                />{' '}
                                            </div>
                                        </div>
                                    </Fragment>
                                ) : (
                                    <p className="text-darwin-neutral-400 text-center">
                                        You can add further details after selecting a medicine
                                    </p>
                                )}
                            </div>

                            <div className="flex items-center justify-between px-16 py-12 rounded-b-8">
                                <p
                                    className="text-neutral-3 font-700 cursor-pointer"
                                    onClick={closeMe}
                                >
                                    Cancel
                                </p>
                                {isMedicineImageUploading && (
                                    <p className="flex items-center space-x-8">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 512 512"
                                            className="w-20 h-20 animate-spin fill-actionPrimary"
                                        >
                                            <path d="M288 28.977v16.391c0 7.477 5.182 13.945 12.474 15.598C389.568 81.162 456 160.742 456 256c0 110.532-89.451 200-200 200-110.532 0-200-89.451-200-200 0-95.244 66.422-174.837 155.526-195.034C218.818 59.313 224 52.845 224 45.368V28.981c0-10.141-9.322-17.76-19.246-15.675C91.959 37.004 7.373 137.345 8.004 257.332c.72 137.052 111.477 246.956 248.531 246.667C393.255 503.711 504 392.788 504 256c0-119.349-84.308-219.003-196.617-242.665C297.403 11.232 288 18.779 288 28.977z" />
                                        </svg>
                                        <span>Uploading medicine image...</span>
                                    </p>
                                )}
                                <div className="flex items-center space-x-8">
                                    <button
                                        className="space-x-8 btn--small secondary-btn px-16 py-8"
                                        onClick={() => {
                                            this.setState({ isSaveAndAddMedLoading: true });
                                            onAddMedicine('Save & Add Medicine Details', true);
                                        }}
                                        disabled={disabledBtns}
                                    >
                                        {isSaveAndAddMedLoading && (
                                            <div className="w-20 h-20">
                                                <svg
                                                    className="circular-loader"
                                                    viewBox="25 25 50 50"
                                                >
                                                    <circle
                                                        className="loader-path"
                                                        cx="50"
                                                        cy="50"
                                                        r="20"
                                                        fill="none"
                                                        stroke="#000000"
                                                        stroke-width="3"
                                                    ></circle>
                                                </svg>
                                            </div>
                                        )}
                                        <span>Save &amp; add another</span>
                                    </button>
                                    <button
                                        className="space-x-8 btn--small primary-btn px-16 py-8"
                                        onClick={() => {
                                            this.setState({ isSaveMedLoading: true });
                                            onAddMedicine('Save Medicine Details');
                                        }}
                                        disabled={disabledBtns}
                                    >
                                        {isSaveMedLoading && (
                                            <div className="w-20 h-20">
                                                <svg
                                                    className="circular-loader"
                                                    viewBox="25 25 50 50"
                                                >
                                                    <circle
                                                        className="loader-path"
                                                        cx="50"
                                                        cy="50"
                                                        r="20"
                                                        fill="none"
                                                        stroke="#000000"
                                                        stroke-width="3"
                                                    ></circle>
                                                </svg>
                                            </div>
                                        )}
                                        <span>Save</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </Hotkeys>
        );
    }
}

export default connect((state: GlobalState) => ({
    elementsConfig: getGenieSetting(state, 'medElements') as GeniePadElementsSettingItem[],
}))(AddMedicines);
